<template>
    <!-- Admin → Marketing → Prospectos de anfitriones (/admin/marketing/crm-anfitriones).
         Tablero tipo Notion con los operadores a los que el equipo comercial les
         escribe. Diseño = demo aprobada (OneDrive\Desktop\nuevo-crm-anfitriones),
         estilos en crmAnfitriones.css (generado, prefijo cr-) + crmAnfitrionesFront.css.
         Datos: CrmAnfitrionesController (GET/POST admin/crm-anfitriones…). Cada
         cambio queda con el admin que lo hizo; se puede asignar a cualquier admin
         (`acceso` = tiene el módulo `crm-anfitriones`). Se refresca solo cada 30 s para ver lo que mueve
         el resto del equipo. -->
    <div ref="raiz" class="cr cr-crm" :class="{ 'is-ready': listo }">
        <!-- Encabezado compacto (como Notion): título + cifras en una línea y una
             barra fina con vistas, búsqueda, Filtros (ventana emergente) y acciones.
             El tablero ocupa el resto de la pantalla y su barra horizontal queda
             siempre visible abajo. -->
        <header class="cr-top">
            <div class="cr-top-title">
                <h1 class="cr-crm-title" :title="t('sub', { n: brokers.filter(b => b.acceso).length })">{{ t('title_a') }} <span class="cr-bk-grad">{{ t('title_b') }}</span></h1>
            </div>
            <dl class="cr-mini">
                <div class="cr-mini-st"><dt>{{ t('stat.total') }}</dt><dd>{{ cifra(tarjetas.length) }}</dd></div>
                <button type="button" class="cr-mini-st cr-mini-st--warn" :class="{ 'is-on': F.solo === 'sin' }" :aria-pressed="String(F.solo === 'sin')" :title="t('stat.pending_s', { n: stats.tarde })" @click="toggleSolo('sin')">
                    <dt>{{ t('stat.pending') }}</dt><dd>{{ cifra(stats.pend) }}</dd>
                </button>
                <div class="cr-mini-st" :title="stats.medidos ? tc('stat.first_s', stats.medidos) : t('stat.first_none')">
                    <dt>{{ t('stat.first') }}</dt><dd>{{ stats.prom === null ? '—' : durTxt(stats.prom) }}</dd>
                </div>
                <button type="button" class="cr-mini-st" :title="stats.ult ? t('stat.today_s', { n: nombreDe(stats.ult.q), t: rel(stats.ult.t) }) : t('stat.today_none')" @click="abrirActividad()">
                    <dt>{{ t('stat.today') }}</dt><dd>{{ cifra(stats.hoy) }}</dd>
                </button>
            </dl>
        </header>

        <section class="cr-bar" :aria-label="t('tb.label')">
            <div class="cr-tb-views" role="tablist" :aria-label="t('tb.view')">
                <button type="button" role="tab" :aria-selected="String(vista === 'tablero')" :title="t('head.hint_board')" @click="cambiarVista('tablero')"><span v-html="I.board"></span>{{ t('tb.board') }}</button>
                <button type="button" role="tab" :aria-selected="String(vista === 'lista')" :title="t('head.hint_list')" @click="cambiarVista('lista')"><span v-html="I.list"></span>{{ t('tb.list') }}</button>
            </div>
            <span class="cr-bar-n">{{ visibles.length === tarjetas.length ? tc('head.n', tarjetas.length) : t('head.of', { a: visibles.length, b: tarjetas.length }) }}</span>
            <span v-if="sincronizando" class="cr-sync"><span v-html="I.reload"></span>{{ t('head.sync') }}</span>
            <span class="cr-bar-sp"></span>
            <label class="cr-tb-search">
                <span v-html="I.search"></span><span class="cr-sr">{{ t('tb.search_label') }}</span>
                <input ref="q" v-model="qTxt" type="search" :placeholder="t('tb.search_ph')" autocomplete="off" />
                <kbd class="cr-tb-kbd" aria-hidden="true">/</kbd>
            </label>
            <button ref="btnFiltros" type="button" class="cr-btn cr-btn-ghost cr-btn-filtros" :class="{ 'is-on': nFiltros }" aria-haspopup="dialog" :aria-expanded="String(filtrosAbiertos)" @click="filtrosAbiertos = !filtrosAbiertos">
                <span v-html="I.filter"></span>{{ t('f.btn') }}<span v-if="nFiltros" class="cr-badge">{{ nFiltros }}</span>
            </button>
            <span class="cr-tb-gap"></span>
            <button type="button" class="cr-btn cr-btn-ghost cr-act-btn" :title="t('activity.btn')" @click="abrirActividad()">
                <span v-html="I.clock"></span><span class="cr-btn-t">{{ t('activity.btn') }}</span><span v-if="actividadNueva" class="cr-act-dot" :title="t('activity.new')"></span>
            </button>
            <button type="button" class="cr-btn cr-btn-main" @click="nueva()"><span v-html="I.plus"></span>{{ t('tb.new') }}</button>
            <!-- Panel admin, Importar y Exportar van en «…» (como en Tableros personales). -->
            <button type="button" class="cr-ico-btn cr-more-btn" aria-haspopup="menu" aria-expanded="false" :aria-label="t('menu.board')" @click="menuTablero($event)" v-html="I.more"></button>
        </section>

        <!-- Filtros activos (se quitan de a uno); solo aparece si hay alguno. -->
        <div v-if="chipsActivos.length" class="cr-activos">
            <button v-for="c in chipsActivos" :key="c.k" type="button" class="cr-activo" :aria-label="t('f.remove', { f: c.t })" @click="quitarFiltro(c.k)">
                {{ c.t }}<span v-html="I.close"></span>
            </button>
            <button type="button" class="cr-btn-link" @click="limpiar">{{ t('f.clear') }}</button>
        </div>

        <CrmFiltros v-if="filtrosAbiertos" :anchor="$refs.btnFiltros" @cerrar="filtrosAbiertos = false" />

        <div class="cr-area">
        <div v-if="cargando && !listo" class="cr-loading"><HyFLoader variant="inline" /></div>
        <div v-else-if="error && !listo" class="cr-list-empty cr-error">
            <h3>{{ t('error.t') }}</h3><p>{{ t('error.p') }}</p>
            <button type="button" class="cr-btn cr-btn-main" @click="cargar()">{{ t('error.retry') }}</button>
        </div>
        <template v-else>
            <div v-if="!tarjetas.length" class="cr-list-empty cr-first">
                <span class="cr-drop-ico" v-html="I.file"></span>
                <h3>{{ t('empty.t') }}</h3><p>{{ t('empty.p') }}</p>
                <button type="button" class="cr-btn cr-btn-main" @click="modal = 'importar'"><span v-html="I.upload"></span>{{ t('tb.import') }}</button>
            </div>
            <CrmBoard v-else-if="vista === 'tablero'" ref="board" />
            <CrmList v-else ref="list" />
        </template>
        </div>

        <CrmDrawer v-if="abiertaId" ref="drawer" :key="'dr'" />
        <CrmNueva v-if="modal === 'nueva'" :estado-inicial="nuevaEn" @cerrar="modal = null" />
        <CrmImportar v-if="modal === 'importar'" @cerrar="modal = null" />
        <CrmActividad v-if="modal === 'actividad'" :quien-inicial="actividadDe" @cerrar="modal = null" />
        <CrmModal v-if="modal === 'exportar'" :titulo="t('export.t')" :sub="t('export.sub')" :mw="480" @cerrar="modal = null">
            <div class="cr-opts">
                <label class="cr-opt"><input v-model="exportarSolo" type="radio" :value="true" /><div><b>{{ t('export.filtered', { n: visibles.length }) }}</b><span>{{ t('export.filtered_s') }}</span></div></label>
                <label class="cr-opt"><input v-model="exportarSolo" type="radio" :value="false" /><div><b>{{ t('export.all', { n: tarjetas.length }) }}</b><span>{{ t('export.all_s') }}</span></div></label>
            </div>
            <template #pie>
                <button type="button" class="cr-btn cr-btn-ghost" @click="modal = null">{{ t('cancel') }}</button>
                <button type="button" class="cr-btn cr-btn-main" @click="exportar(exportarSolo)">{{ t('export.go') }}</button>
            </template>
        </CrmModal>
        <CrmModal v-if="confirmacion" :titulo="confirmacion.titulo" :mw="440" @cerrar="responder(false)">
            <p>{{ confirmacion.texto }}</p>
            <template #pie>
                <button type="button" class="cr-btn cr-btn-ghost" @click="responder(false)">{{ t('cancel') }}</button>
                <button ref="si" type="button" class="cr-btn" :class="confirmacion.peligro ? 'cr-btn-danger-main' : 'cr-btn-main'" @click="responder(true)">{{ confirmacion.si }}</button>
            </template>
        </CrmModal>

        <CrmPop ref="pop" />
        <div class="cr-toasts" aria-live="polite">
            <div v-for="m in toasts" :key="m.id" class="cr-toast" :class="{ 'cr-toast--plain': !m.accion, 'is-out': m.out }">
                <span v-html="m.mal ? I.warn : I.check"></span><span v-html="m.html"></span>
                <button v-if="m.accion" type="button" @click="accionToast(m)">{{ m.accion }}</button>
            </div>
        </div>
    </div>
</template>


<script>
import axios from 'axios';
import HyFLoader from '@/components/Loader/HyFLoader.vue';
import CrmPop from './CrmPop.vue';
import CrmModal from './CrmModal.vue';
import CrmBoard from './CrmBoard.vue';
import CrmList from './CrmList.vue';
import CrmDrawer from './CrmDrawer.vue';
import CrmNueva from './CrmNueva.vue';
import CrmImportar from './CrmImportar.vue';
import CrmActividad from './CrmActividad.vue';
import CrmFiltros from './CrmFiltros.vue';
import { I, DAY, esc, norm, dur, fecha, locale, nivelEdad, iniciales, ciudadesDe, PRI_IC, COLORES, descargarExcel } from './crmUtil';
import { t, tc } from './textos';

const API = () => `${process.env.VUE_APP_API_URL}admin/crm-anfitriones`;
const REFRESCO_MS = 30000;
let idToast = 0;

export default {
    name: 'CrmAnfitriones',
    components: { HyFLoader, CrmPop, CrmModal, CrmBoard, CrmList, CrmDrawer, CrmNueva, CrmImportar, CrmActividad, CrmFiltros },
    provide() { return { crm: this }; },

    data() {
        return {
            I,
            cargando: true, listo: false, error: false, sincronizando: false,
            estados: [], tarjetas: [], brokers: [], yo: null,
            plegadas: [], vista: 'tablero', actividadNueva: false,
            desfase: 0, ahora: Date.now(),
            qTxt: '', F: { q: '', pri: '', asig: '', tipo: '', ciudad: '', solo: '' },
            abiertaId: null, tabDetalle: 'resumen',
            modal: null, filtrosAbiertos: false, nuevaEn: null, actividadDe: '', exportarSolo: true,
            confirmacion: null, toasts: [],
            pendientes: 0,
        };
    },

    computed: {
        porId() { const m = {}; this.tarjetas.forEach(c => { m[c.id] = c; }); return m; },
        estadoPorId() { const m = {}; this.estados.forEach(e => { m[e.id] = e; }); return m; },
        brokerPorId() { const m = {}; this.brokers.forEach((b, i) => { m[b.id] = { ...b, color: COLORES[i % COLORES.length] }; }); return m; },
        u() { return { min: this.t('u.min'), h: this.t('u.h'), d: this.t('u.d') }; },
        loc() { return locale('es'); },
        visibles() { return this.tarjetas.filter(this.pasa); },
        hayFiltros() { return Object.keys(this.F).some(k => !!this.F[k]); },
        /** Filtros de la ventana (la búsqueda va aparte). */
        nFiltros() { return ['pri', 'asig', 'tipo', 'ciudad', 'solo'].filter(k => !!this.F[k]).length; },
        chipsActivos() {
            const F = this.F, out = [];
            if (F.pri) out.push({ k: 'pri', t: this.t('f.pri') + ': ' + this.t('pri.' + F.pri) });
            if (F.asig) out.push({ k: 'asig', t: this.t('f.asig') + ': ' + (F.asig === 'nadie' ? this.t('f.nobody') : this.nombreDe(Number(F.asig))) });
            if (F.ciudad) out.push({ k: 'ciudad', t: this.t('f.city') + ': ' + ((this.ciudades.find(x => x.v === F.ciudad) || {}).t || F.ciudad) });
            if (F.tipo) out.push({ k: 'tipo', t: this.t('f.type') + ': ' + F.tipo });
            if (F.solo) out.push({ k: 'solo', t: this.t({ sin: 'stat.pending', tarde: 'f.late', seg: 'f.follow' }[F.solo]) });
            if (F.q) out.push({ k: 'q', t: '“' + F.q + '”' });
            return out;
        },
        conteoPri() { const n = { alta: 0, media: 0, baja: 0 }; this.tarjetas.forEach(c => { if (n[c.prioridad] !== undefined) n[c.prioridad]++; }); return n; },
        ciudades() {
            const cont = {};
            this.tarjetas.forEach(c => ciudadesDe(c).forEach(x => { const k = norm(x); cont[k] = cont[k] || { n: 0, t: x }; cont[k].n++; }));
            return Object.keys(cont).map(k => ({ v: k, t: cont[k].t, n: cont[k].n })).sort((a, b) => b.n - a.n || a.t.localeCompare(b.t));
        },
        tipos() {
            const cont = {};
            this.tarjetas.forEach(c => { if (c.tipo) cont[c.tipo] = (cont[c.tipo] || 0) + 1; });
            return Object.keys(cont).sort().map(k => ({ v: k, t: k, n: cont[k] }));
        },
        filtrosDD() {
            const busca = (lista, v) => (lista.find(x => x.v === v) || {}).t;
            const asig = [{ v: 'nadie', t: this.t('f.nobody') }].concat(this.brokers.map(b => ({ v: String(b.id), t: b.nombre, html: this.whoHTML(b.id) })));
            return [
                { k: 'asig', titulo: this.t('f.asig'), todos: this.t('f.any'), items: asig, actual: busca(asig, this.F.asig) || this.t('f.any') },
                { k: 'ciudad', titulo: this.t('f.city'), todos: this.t('f.all_f'), buscar: true, items: this.ciudades.map(x => ({ v: x.v, t: x.t, sub: String(x.n) })), actual: busca(this.ciudades, this.F.ciudad) || this.t('f.all_f') },
                { k: 'tipo', titulo: this.t('f.type'), todos: this.t('f.all_m'), buscar: true, items: this.tipos.map(x => ({ v: x.v, t: x.t, sub: String(x.n) })), actual: this.F.tipo || this.t('f.all_m') },
            ];
        },
        stats() {
            const T = this.tarjetas, pend = T.filter(c => this.estado(c.estado).pend);
            const medidos = T.filter(c => c.primerContacto);
            const hoy = new Date(this.ahora); hoy.setHours(0, 0, 0, 0);
            const movs = T.filter(c => c.movida && c.movida >= +hoy);
            const ult = movs.slice().sort((a, b) => b.movida - a.movida)[0];
            return {
                pend: pend.length,
                tarde: pend.filter(c => this.ahora - c.estadoDesde >= 7 * DAY).length,
                medidos: medidos.length,
                prom: medidos.length ? medidos.reduce((s, c) => s + (c.primerContacto - c.creada), 0) / medidos.length : null,
                hoy: movs.length,
                ult: ult ? { q: ult.movidaPor, t: ult.movida } : null,
                cerradas: T.filter(c => this.estado(c.estado).clave === 'cerrado').length,
                enCurso: T.filter(c => { const e = this.estado(c.estado); return !e.pend && !e.fin && e.clave !== 'contactado'; }).length,
            };
        },
    },

    watch: {
        qTxt(v) { clearTimeout(this._tq); this._tq = setTimeout(() => { this.F.q = v.trim(); }, 120); },
        modal(v) { this.bloquear(); if (!v) this.$nextTick(() => this._focoModal && this._focoModal.isConnected && this._focoModal.focus()); else this._focoModal = document.activeElement; },
        abiertaId() { this.bloquear(); },
    },

    created() {
        const q = this.$route.query;
        if (q.vista === 'lista') this.vista = 'lista';
        this.cargar();
    },
    mounted() {
        // La página ocupa exactamente la pantalla debajo del header del sitio (sin
        // scroll de página): así la barra horizontal del tablero siempre se ve abajo.
        this._alto = () => this.ajustarAlto();
        this.ajustarAlto();
        window.addEventListener('resize', this._alto);
        this.$nextTick(this.ajustarAlto);
        this._tick = setInterval(() => { this.ahora = Date.now() + this.desfase; }, 60000);
        this._poll = setInterval(() => this.refrescar(), REFRESCO_MS);
        this._vis = () => { if (document.visibilityState === 'visible') this.refrescar(); };
        document.addEventListener('visibilitychange', this._vis);
        this._key = e => this.teclaGlobal(e);
        document.addEventListener('keydown', this._key);
        // Si se cierra o recarga la pestaña con un cambio todavía en camino, avisar (se perdería).
        this._unload = e => { if (this.pendientes > 0 || (this.$refs.drawer && this.$refs.drawer.sucio)) { e.preventDefault(); e.returnValue = ''; } };
        window.addEventListener('beforeunload', this._unload);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this._alto);
        window.removeEventListener('beforeunload', this._unload);
        clearInterval(this._tick); clearInterval(this._poll); clearTimeout(this._tq);
        document.removeEventListener('visibilitychange', this._vis);
        document.removeEventListener('keydown', this._key);
        document.documentElement.classList.remove('cr-locked', 'cr-dragging');
    },
    beforeRouteLeave(to, from, next) {
        if (this.$refs.drawer && this.$refs.drawer.sucio && !window.confirm(this.t('dirty.leave'))) return next(false);
        next();
    },

    methods: {
        /** DefaultLayout deja un padding arriba (60 px) además del header (72 px): se compensa. */
        ajustarAlto() {
            const el = this.$el; if (!el || !el.style) return;
            const cont = el.closest('.main-content');
            const pad = cont ? parseFloat(getComputedStyle(cont).paddingTop) || 0 : 0;
            const hdr = document.querySelector('.app-header');
            const alto = hdr ? Math.round(hdr.getBoundingClientRect().height) : 60;
            el.style.marginTop = (-pad) + 'px';
            el.style.setProperty('--crm-hd', alto + 'px');
        },
        // ── Textos ──
        t(k, p) { return t(k, p); },
        tc(k, n, p) { return tc(k, n, p); },
        cifra(n) { return Number(n || 0).toLocaleString(this.loc); },
        durTxt(ms) { return dur(ms, this.u); },
        fecha(ts, hora = true) { return fecha(ts, this.loc, hora); },
        rel(ts) {
            const ms = this.ahora - ts;
            if (ms < 6e4) return this.t('rel.now');
            if (ms < DAY) return this.t('rel.ago', { t: dur(ms, this.u) });
            const hoy = new Date(this.ahora); hoy.setHours(0, 0, 0, 0);
            if (ts >= hoy - DAY) return this.t('rel.yesterday');
            if (ms < 30 * DAY) return this.t('rel.ago', { t: Math.floor(ms / DAY) + ' ' + this.u.d });
            return this.fecha(ts, false);
        },
        diaNombre(ts) {
            const hoy = new Date(this.ahora); hoy.setHours(0, 0, 0, 0);
            if (ts >= +hoy) return this.t('rel.today');
            if (ts >= hoy - DAY) return this.t('rel.yesterday_c');
            return this.fecha(ts, false);
        },

        // ── Datos ──
        estado(id) { return this.estadoPorId[id] || this.estados[0] || { id: 0, nombre: '', tn: 'gray' }; },
        tarjeta(id) { return this.porId[id]; },
        nombreDe(id) { if (!id) return this.t('import_user'); const b = this.brokerPorId[id]; return b ? b.nombre : this.t('unknown_user'); },
        edad(c) { const ms = Math.max(0, this.ahora - c.estadoDesde); return { ms, txt: dur(ms, this.u), nivel: nivelEdad(ms, this.estado(c.estado)) }; },
        segVencido(c) { return !!c.seguimiento && new Date(c.seguimiento + 'T23:59:59').getTime() < this.ahora; },
        enColumna(id) { return this.visibles.filter(c => c.estado === id).sort((a, b) => a.orden - b.orden || a.id - b.id); },
        pasa(c) {
            const F = this.F;
            if (F.pri && c.prioridad !== F.pri) return false;
            if (F.asig === 'nadie' ? c.asignado : F.asig && String(c.asignado) !== F.asig) return false;
            if (F.tipo && c.tipo !== F.tipo) return false;
            if (F.ciudad && !ciudadesDe(c).some(x => norm(x) === F.ciudad)) return false;
            if (F.solo === 'sin' && !this.estado(c.estado).pend) return false;
            if (F.solo === 'seg' && !this.segVencido(c)) return false;
            if (F.solo === 'tarde' && this.edad(c).nivel !== 'late') return false;
            if (F.q) {
                const txt = norm([c.empresa, c.contacto, c.cargo, c.ciudades, c.email, c.web, c.tipo].join(' '));
                if (!norm(F.q).split(' ').every(p => txt.includes(p))) return false;
            }
            return true;
        },

        // ── Píldoras (HTML con todo escapado) ──
        estPill(id) { const e = this.estado(id); return `<span class="cr-est tn-${esc(e.tn)}"><span class="cr-dot"></span>${esc(e.nombre)}</span>`; },
        priPill(k) { k = k || ''; return `<span class="cr-pri cr-pri--${k || 'none'}">${PRI_IC[k]}${esc(this.t('pri.' + (k || 'none')))}</span>`; },
        avHTML(id, cls = '') {
            const b = id ? this.brokerPorId[id] : null;
            if (!b) return `<span class="cr-av cr-av--x ${cls}" aria-hidden="true">${id ? '?' : I.file.replace('<svg', '<svg width="12" height="12"')}</span>`;
            return `<span class="cr-av ${cls}" style="--av:${b.color}" title="${esc(b.nombre)}" aria-hidden="true">${esc(iniciales(b.nombre))}</span>`;
        },
        whoHTML(id) {
            return id ? `<span class="cr-who">${this.avHTML(id, 'cr-av--s')}${esc(this.nombreDe(id))}</span>` : `<span class="cr-who is-none">${esc(this.t('f.nobody'))}</span>`;
        },

        // ── Carga y refresco ──
        async cargar(silencioso = false) {
            if (!silencioso) { this.cargando = true; this.error = false; }
            try {
                const { data } = await axios.get(API());
                this.aplicar(data);
                this.listo = true;
            } catch (e) {
                if (!silencioso) { console.error('[CRM] No se pudo cargar', e); this.error = true; }
            } finally {
                this.cargando = false;
            }
        },
        aplicar(d) {
            this.desfase = (d.ahora || Date.now()) - Date.now();
            this.ahora = Date.now() + this.desfase;
            this.estados = d.estados || [];
            this.brokers = d.brokers || [];
            this.yo = d.yo;
            this.actividadNueva = !!d.actividad_nueva;
            if (!this.listo) {
                this.plegadas = (d.ajustes && d.ajustes.plegadas) || [];
                if (!this.$route.query.vista && !this._vistaElegida && d.ajustes && d.ajustes.vista) this.vista = d.ajustes.vista;
            }
            const antes = this.abiertaId ? this.porId[this.abiertaId] : null;
            this.tarjetas = d.tarjetas || [];
            if (this.abiertaId) {
                const ahora = this.porId[this.abiertaId];
                if (!ahora) { this.abiertaId = null; this.toast(this.t('toast.gone'), null, null, true); }
                else if (antes && ahora.ultimo && antes.ultimo && ahora.ultimo.t !== antes.ultimo.t && this.$refs.drawer) this.$refs.drawer.cargarHistorial();
            }
        },
        async refrescar() {
            // No se refresca en medio de un arrastre, con cambios en vuelo o con la pestaña oculta.
            if (!this.listo || this.pendientes || document.visibilityState !== 'visible') return;
            if (this.$refs.board && this.$refs.board.arrastrando) return;
            this.sincronizando = true;
            try { const { data } = await axios.get(API()); if (!this.pendientes) this.aplicar(data); } catch (e) { /* se reintenta en el próximo ciclo */ }
            this.sincronizando = false;
        },
        async api(metodo, ruta, datos) {
            this.pendientes++;
            try {
                const { data } = await axios({ method: metodo, url: API() + ruta, data: datos });
                return data;
            } finally {
                this.pendientes--;
            }
        },
        reemplazar(nueva) {
            const i = this.tarjetas.findIndex(c => c.id === nueva.id);
            if (i >= 0) this.$set(this.tarjetas, i, nueva); else this.tarjetas.push(nueva);
        },
        fallo(e, msg) {
            const r = e && e.response;
            console.error('[CRM]', msg, e);
            this.toast(esc(r && r.status === 403 ? this.t('error.forbidden') : r && r.data && r.data.message && r.status !== 500 ? r.data.message : this.t(msg || 'error.save')), null, null, true);
        },

        // ── Acciones (cambio en pantalla al instante; si la API falla, se vuelve atrás) ──
        async mover(c, estId, orden) {
            if (!c || (c.estado === estId && orden === undefined)) return;
            const cambiaCol = c.estado !== estId;
            if (orden === undefined) { const cs = this.tarjetas.filter(x => x.estado === estId && x.id !== c.id); orden = cs.length ? Math.min(...cs.map(x => x.orden)) - 1 : 0; }
            const antes = { ...c };
            const nueva = { ...c, estado: estId, orden };
            if (cambiaCol) Object.assign(nueva, { estadoDesde: this.ahora, movida: this.ahora, movidaPor: this.yo, ultimo: { t: this.ahora, q: this.yo, ty: 'mov' } });
            this.reemplazar(nueva);
            if (cambiaCol) this.$nextTick(() => this.$refs.board && this.$refs.board.resaltar(c.id));
            try {
                const r = await this.api('post', `/${c.id}/mover`, { estado_id: estId, orden });
                this.reemplazar(r.tarjeta);
                if (cambiaCol) {
                    this.toast(`<b>${esc(c.empresa)}</b> → ${esc(this.estado(estId).nombre)}`, this.t('undo'), async () => {
                        try { const d = await this.api('post', `/${c.id}/deshacer`, { historial_id: r.historial_id }); this.reemplazar(d.tarjeta); this.toast(esc(this.t('toast.undone'))); this.recargarHistorial(c.id); }
                        catch (e) { this.fallo(e, 'error.undo'); }
                    });
                    this.recargarHistorial(c.id);
                }
            } catch (e) {
                this.reemplazar(antes);
                this.fallo(e, 'error.move');
            }
        },
        async cambio(c, ruta, datos, local, aviso) {
            const antes = { ...c };
            this.reemplazar({ ...c, ...local, ultimo: { t: this.ahora, q: this.yo, ty: 'x' } });
            try {
                const r = await this.api('post', `/${c.id}/${ruta}`, datos);
                this.reemplazar(r.tarjeta);
                if (aviso) this.toast(aviso);
                this.recargarHistorial(c.id);
            } catch (e) { this.reemplazar(antes); this.fallo(e); }
        },
        prioridad(c, p) { if (c.prioridad !== p) this.cambio(c, 'prioridad', { prioridad: p || null }, { prioridad: p }, `<b>${esc(c.empresa)}</b> · ${esc(this.t('toast.pri', { p: this.t('pri.' + (p || 'none')).toLowerCase() }))}`); },
        asignar(c, id) {
            id = id ? Number(id) : null;
            if (c.asignado === id) return;
            this.cambio(c, 'asignar', { asignado_id: id }, { asignado: id }, id ? `<b>${esc(c.empresa)}</b> ${esc(this.t('toast.assigned', { n: this.nombreDe(id) }))}` : `<b>${esc(c.empresa)}</b> ${esc(this.t('toast.unassigned'))}`);
        },
        seguimiento(c, f) { if ((c.seguimiento || '') !== (f || '')) this.cambio(c, 'seguimiento', { fecha: f || null }, { seguimiento: f || '' }, esc(this.t(f ? 'toast.follow' : 'toast.follow_off'))); },
        async nota(c, texto) {
            const r = await this.api('post', `/${c.id}/notas`, { texto });
            this.reemplazar(r.tarjeta);
            this.toast(esc(this.t('toast.note')));
        },
        async editar(c, datos) {
            const r = await this.api('put', `/${c.id}`, datos);
            this.reemplazar(r.tarjeta);
            if (r.cambios) this.toast(esc(this.t('toast.saved')));
            return r;
        },
        async crear(datos) {
            const r = await this.api('post', '', datos);
            this.reemplazar(r.tarjeta);
            this.$nextTick(() => this.$refs.board && this.$refs.board.resaltar(r.tarjeta.id, true));
            this.toast(`<b>${esc(r.tarjeta.empresa)}</b> ${esc(this.t('toast.created', { e: this.estado(r.tarjeta.estado).nombre }))}`, this.t('open'), () => this.abrir(r.tarjeta.id));
            return r.tarjeta;
        },
        async eliminar(c) {
            const ok = await this.confirmar({ titulo: this.t('del.t', { e: c.empresa }), texto: this.t('del.p'), si: this.t('del.go'), peligro: true });
            if (!ok) return;
            try {
                await this.api('delete', `/${c.id}`);
                this.abiertaId = null;
                this.tarjetas = this.tarjetas.filter(x => x.id !== c.id);
                this.toast(`<b>${esc(c.empresa)}</b> ${esc(this.t('toast.deleted'))}`, this.t('undo'), async () => {
                    try { const r = await this.api('post', `/${c.id}/restaurar`); this.reemplazar(r.tarjeta); } catch (e) { this.fallo(e); }
                });
            } catch (e) { this.fallo(e); }
        },
        recargarHistorial(id) { if (this.abiertaId === id && this.$refs.drawer) this.$refs.drawer.cargarHistorial(); },
        guardarAjustes(datos) { axios.put(API() + '/ajustes', datos).catch(() => {}); },
        plegar(id) {
            this.plegadas = this.plegadas.includes(id) ? this.plegadas.filter(x => x !== id) : this.plegadas.concat(id);
            this.guardarAjustes({ plegadas: this.plegadas });
        },
        cambiarVista(v) {
            this._vistaElegida = true; // si se elige mientras carga, no la pisa la vista guardada
            if (this.vista === v) { if (!this.listo) this.guardarAjustes({ vista: v }); return; }
            this.vista = v;
            this.guardarAjustes({ vista: v });
            const query = { ...this.$route.query };
            if (v === 'lista') query.vista = 'lista'; else delete query.vista;
            this.$router.replace({ query }).catch(() => {});
        },

        // ── Panel, modales, menús ──
        abrir(id, tab) {
            if (this.abiertaId === id) { if (tab) this.tabDetalle = tab; return; }
            const ir = () => { this.tabDetalle = tab || 'resumen'; this.abiertaId = id; };
            if (this.$refs.drawer && this.$refs.drawer.sucio) {
                this.confirmar({ titulo: this.t('dirty.t'), texto: this.t('dirty.p'), si: this.t('dirty.go'), peligro: true }).then(ok => { if (ok) ir(); });
            } else ir();
        },
        cerrarDetalle() { this.abiertaId = null; },
        nueva(estId) { this.nuevaEn = estId || null; this.modal = 'nueva'; },
        abrirActividad(quien = '') {
            this.actividadDe = quien; this.modal = 'actividad'; this.actividadNueva = false;
            this.guardarAjustes({ visto: true });
        },
        abrirPop(cfg) { this.$refs.pop.abrir(cfg); },
        popAbierto(anchor) { return this.$refs.pop && this.$refs.pop.abierto(anchor); },
        cerrarPop() { if (this.$refs.pop) this.$refs.pop.cerrar(false); },
        abrirFiltro(ev, d) {
            const a = ev.currentTarget;
            if (this.popAbierto(a)) return this.cerrarPop();
            this.abrirPop({ anchor: a, titulo: d.titulo, buscar: d.buscar, w: d.buscar ? 300 : 240, val: this.F[d.k],
                items: [{ v: '', t: d.todos }].concat(d.items), onPick: v => { this.F[d.k] = v; } });
        },
        /** Desplegable de estado / prioridad / asignada de una tarjeta. */
        ddTarjeta(ev, c, campo) {
            const a = ev.currentTarget;
            if (this.popAbierto(a)) return this.cerrarPop();
            if (campo === 'estado') {
                this.abrirPop({ anchor: a, titulo: this.t('p.state'), w: 250, val: c.estado, items: this.estados.map(e => ({ v: e.id, t: e.nombre, html: this.estPill(e.id) })), onPick: v => this.mover(this.tarjeta(c.id), v) });
            } else if (campo === 'pri') {
                this.abrirPop({ anchor: a, titulo: this.t('p.pri'), w: 200, val: c.prioridad || '', items: ['alta', 'media', 'baja', ''].map(k => ({ v: k, t: this.t('pri.' + (k || 'none')), html: this.priPill(k) })), onPick: v => this.prioridad(this.tarjeta(c.id), v) });
            } else {
                const items = [{ v: null, t: this.t('f.nobody'), html: this.whoHTML(null) }].concat(this.brokers.filter(b => !b.sin_acceso).map(b => ({ v: b.id, t: b.nombre, html: this.whoHTML(b.id) })));
                this.abrirPop({ anchor: a, titulo: this.t('p.asig'), w: 250, val: c.asignado || null, items, onPick: v => this.asignar(this.tarjeta(c.id), v) });
            }
        },
        menuTarjeta(ev, c) {
            const a = ev.currentTarget;
            if (this.popAbierto(a)) return this.cerrarPop();
            const items = this.estados.map(e => ({ v: 'e' + e.id, t: e.nombre, html: `<span class="cr-dot tn-${esc(e.tn)}"></span>${esc(e.nombre)}`, actual: e.id === c.estado }))
                .concat([{ sep: true }, { v: 'me', t: c.asignado === this.yo ? this.t('menu.unassign') : this.t('menu.assign'), ic: I.user }, { v: 'open', t: this.t('menu.open'), ic: I.ext },
                    { v: 'new', t: this.t('menu.new_here', { e: this.estado(c.estado).nombre }), ic: I.plus }, { v: 'dup', t: this.t('menu.dup'), ic: I.copy },
                    { sep: true }, { v: 'del', t: this.t('menu.del'), ic: I.trash, danger: true }]);
            this.abrirPop({ anchor: a, menu: true, titulo: this.t('menu.move'), w: 260, alinear: 'derecha', items, onPick: v => {
                const x = this.tarjeta(c.id);
                if (!x) return;
                if (v === 'me') this.asignar(x, x.asignado === this.yo ? null : this.yo);
                else if (v === 'open') this.abrir(x.id);
                else if (v === 'new') this.nueva(x.estado);
                else if (v === 'dup') this.duplicar(x);
                else if (v === 'del') this.eliminar(x);
                else if (Number(v.slice(1)) !== x.estado) this.mover(x, Number(v.slice(1)));
            } });
        },
        /** Nuevo orden de las columnas (arrastre). Es el mismo para todo el equipo. */
        async ordenarEstados(ids, movida) {
            const antes = this.estados;
            this.estados = ids.map(id => this.estadoPorId[id]).filter(Boolean).map((e, i) => ({ ...e, o: i + 1 }));
            try {
                const r = await this.api('put', '/estados-orden', { ids });
                if (r.estados) this.estados = r.estados;
                const e = this.estado(movida);
                this.toast(esc(this.t('toast.cols', { e: e.nombre, n: ids.indexOf(movida) + 1 })));
            } catch (e) { this.estados = antes; this.fallo(e); }
        },        /** Copia la tarjeta justo debajo de la original («Empresa (2)», «(3)»…). */
        async duplicar(c) {
            try {
                const r = await this.api('post', `/${c.id}/duplicar`);
                this.reemplazar(r.tarjeta);
                this.$nextTick(() => this.$refs.board && this.$refs.board.resaltar(r.tarjeta.id, true));
                this.toast(`<b>${esc(r.tarjeta.empresa)}</b> ${esc(this.t('toast.duplicated'))}`, this.t('open'), () => this.abrir(r.tarjeta.id));
            } catch (e) { this.fallo(e); }
        },
        menuTablero(ev) {
            const a = ev.currentTarget;
            if (this.popAbierto(a)) return this.cerrarPop();
            this.abrirPop({ anchor: a, menu: true, titulo: this.t('title_a') + ' ' + this.t('title_b'), w: 240, alinear: 'derecha', items: [
                { v: 'admin', t: this.t('menu.admin'), ic: I.back },
                { sep: true },
                { v: 'imp', t: this.t('tb.import'), ic: I.upload },
                { v: 'exp', t: this.t('tb.export'), ic: I.download },
            ], onPick: v => {
                if (v === 'admin') this.$router.push({ name: 'admin' }).catch(() => {});
                else if (v === 'imp') this.modal = 'importar';
                else if (v === 'exp' && this.tarjetas.length) this.pedirExportar();
            } });
        },        toggleSolo(k) { this.F.solo = this.F.solo === k ? '' : k; },
        quitarFiltro(k) { this.F[k] = ''; if (k === 'q') this.qTxt = ''; },
        limpiar() { Object.keys(this.F).forEach(k => { this.F[k] = ''; }); this.qTxt = ''; },
        confirmar(o) {
            return new Promise(res => {
                this.confirmacion = { ...o, res };
                this.$nextTick(() => this.$refs.si && this.$refs.si.focus());
            });
        },
        responder(v) { const c = this.confirmacion; this.confirmacion = null; if (c) c.res(v); },
        bloquear() { document.documentElement.classList.toggle('cr-locked', !!(this.modal || this.abiertaId || this.confirmacion)); },
        teclaGlobal(e) {
            if (e.key === '/' && !/INPUT|TEXTAREA|SELECT/.test(document.activeElement.tagName) && !this.modal && !this.abiertaId && this.$refs.q) { e.preventDefault(); this.$refs.q.focus(); }
        },

        // ── Avisos ──
        toast(html, accion, fn, mal = false) {
            const m = { id: ++idToast, html, accion, fn, mal, out: false };
            this.toasts.push(m);
            if (this.toasts.length > 3) this.toasts.splice(0, this.toasts.length - 3);
            setTimeout(() => this.quitarToast(m), accion ? 6500 : 4200);
        },
        quitarToast(m) { m.out = true; setTimeout(() => { this.toasts = this.toasts.filter(x => x !== m); }, 200); },
        accionToast(m) { this.quitarToast(m); m.fn(); },

        // ── Exportar (mismas columnas del Excel + las del CRM; se puede volver a importar) ──
        pedirExportar() { if (this.hayFiltros) { this.exportarSolo = true; this.modal = 'exportar'; } else this.exportar(false); },
        async exportar(solo) {
            this.modal = null;
            let hist = [];
            try { hist = (await axios.get(API() + '/exportar')).data.historial || []; } catch (e) { this.fallo(e, 'error.export'); return; }
            const lista = (solo ? this.visibles : this.tarjetas).slice().sort((a, b) => this.estado(a.estado).o - this.estado(b.estado).o || a.orden - b.orden);
            const ids = new Set(lista.map(c => c.id));
            const notas = {};
            hist.forEach(h => { if (h.ty === 'nota' && ids.has(h.tarjeta)) (notas[h.tarjeta] = notas[h.tarjeta] || []).push(`[${this.fecha(h.t)} · ${this.nombreDe(h.q)}] ${h.txt}`); });
            const X = k => this.t('xl.' + k);
            const filas = lista.map(c => ({
                Empresa: c.empresa, Ciudades: c.ciudades, 'Tipo de operador': c.tipo, Web: c.web, Email: c.email, 'Nombre contacto': c.contacto, Cargo: c.cargo,
                'LinkedIn personal': c.liP, Prioridad: { alta: '▲ Alta', media: '● Media', baja: '▼ Baja' }[c.prioridad] || '', Status: this.estado(c.estado).valor, 'LinkedIn empresa': c.liE,
                'Asignada a': c.asignado ? this.nombreDe(c.asignado) : '', [X('since')]: this.fecha(c.estadoDesde), [X('days')]: Math.floor((this.ahora - c.estadoDesde) / DAY),
                [X('first')]: c.primerContacto ? +((c.primerContacto - c.creada) / DAY).toFixed(1) : '', [X('follow')]: c.seguimiento,
                [X('moved_by')]: c.movida ? this.nombreDe(c.movidaPor) : '', [X('last')]: this.fecha(c.ultimo.t), [X('last_by')]: this.nombreDe(c.ultimo.q),
                [X('notes')]: (notas[c.id] || []).reverse().join('\n'),
            }));
            const porT = this.porId;
            const histFilas = hist.filter(h => ids.has(h.tarjeta)).map(h => ({
                [X('h_date')]: this.fecha(h.t), Empresa: (porT[h.tarjeta] || {}).empresa, [X('h_who')]: this.nombreDe(h.q), [X('h_what')]: this.t('hist.kind.' + h.ty), [X('h_detail')]: this.detalleTexto(h),
            }));
            const d = new Date(), f = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            const nombre = `House_and_Flats_PROSPECTOS_CRM_${f}.xlsx`;
            descargarExcel(nombre, [
                { nombre: 'Prospectos H&F', filas, anchos: [28, 30, 34, 30, 30, 22, 22, 30, 12, 22, 30, 18, 20, 12, 14, 12, 18, 20, 18, 60] },
                { nombre: X('h_sheet'), filas: histFilas, anchos: [20, 28, 18, 14, 60] },
            ]);
            this.toast(this.t('toast.exported', { n: filas.length, f: esc(nombre) }));
        },
        detalleTexto(h) {
            if (h.ty === 'mov') return this.estado(h.de).nombre + ' → ' + this.estado(h.a).nombre;
            if (h.ty === 'new') return h.txt || this.estado(h.a).nombre;
            if (h.ty === 'nota') return h.txt;
            if (h.ty === 'asig') return h.a ? this.nombreDe(h.a) : this.t('f.nobody');
            if (h.ty === 'pri') return this.t('pri.' + (h.de || 'none')) + ' → ' + this.t('pri.' + (h.a || 'none'));
            if (h.ty === 'seg') return h.a || this.t('hist.removed');
            return (h.cambios || []).map(x => this.t('fld.' + x.k) + ': ' + (x.a || '—')).join('; ');
        },
    },
};
</script>

<style src="./crmAnfitriones.css"></style>
<style src="./crmAnfitrionesFront.css"></style>
